/**
 * 矢量图形元素组件（心形、多边形、星形、自定义路径）。支持双击进入文字编辑模式。
 *
 * 渲染策略：
 *  - viewBox="0 0 100 100" + preserveAspectRatio="none" 将路径拉伸至元素尺寸
 *  - vectorEffect="non-scaling-stroke" 使描边宽始终以屏幕像素为单位，
 *    不随元素缩放或长宽比变化而改变，彻底避免描边失真问题
 *  - strokeWidth 直接存储屏幕像素值，无需在 viewBox 空间内归一化
 */
import { useState, useCallback } from 'react';
import type { VectorElement as VectorElementType } from '@/components/canvas/document/publicTypes';
import type { ElementComponentProps } from '@/components/canvas/elements/types';
import { getVectorPath, getDashArray } from '@/components/canvas/elements/shape/vector/vectorPaths';
import { appendBlurFilter, composeContainerTransform } from '../../group/containerTransform';
import { blurRadius, elementFillCss, firstShadow, firstStroke } from '@/components/canvas/elements/model';
import { useCanvas } from '@/components/canvas/designer/shell/CanvasContext';
import { ShapeTextEditor } from '../components/ShapeTextEditor';
import { resolveShapeTextStyle } from '../utils/shapeTextStyle';
import { createTextContentData } from '@/components/canvas/elements/text/factory';

export function VectorElement({
  element,
  dragOffset = { dx: 0, dy: 0 },
  onSelect,
  onMouseDown,
}: ElementComponentProps<VectorElementType>) {
  const { dx, dy } = dragOffset;
  const { updateElement, setEditingTableCellId } = useCanvas();
  const [isEditing, setIsEditing] = useState(false);

  const el = element;
  const { width, height } = el.size;
  const stroke = firstStroke(el.appearance);
  const strokeW = stroke?.width ?? 0;
  const textStyle = resolveShapeTextStyle(element);

  const pathD = getVectorPath(el.data.shape, {
    polygonSides: el.data.polygonSides,
    starPoints: el.data.starPoints,
    starInnerRatio: el.data.starInnerRatio,
    pathData: el.data.pathData,
  });

  const dashArray = getDashArray(stroke?.style === 'double' ? 'solid' : stroke?.style, strokeW);

  const shadowEffect = firstShadow(el.appearance);
  const shadow = shadowEffect
    ? `drop-shadow(${shadowEffect.offsetX}px ${shadowEffect.offsetY}px ${shadowEffect.blur}px ${shadowEffect.color})`
    : undefined;

  const wrapperStyle: React.CSSProperties = {
    position: 'absolute',
    left: el.layout.x + dx,
    top: el.layout.y + dy,
    width,
    height,
    opacity: el.appearance.opacity,
    transform: composeContainerTransform(el),
    filter: appendBlurFilter(shadow, blurRadius(el)),
    cursor: 'inherit',
    overflow: 'visible',
  };

  const svgStyle: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    overflow: 'visible',
  };

  const fillCss = elementFillCss(el);
  const fillValue = fillCss && fillCss !== 'none' ? fillCss : 'none';

  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(true);
  }, []);

  const handleEditStart = useCallback(() => {
    setEditingTableCellId(element.id);
  }, [element.id, setEditingTableCellId]);

  const handleEditEnd = useCallback(() => {
    setIsEditing(false);
    setEditingTableCellId(null);
  }, [setEditingTableCellId]);

  const handleCommit = useCallback((text: string) => {
    updateElement(element.id, {
      data: {
        ...element.data,
        text: text ? createTextContentData({ content: text }) : undefined,
      },
    });
  }, [element.id, element.data, updateElement]);

  return (
    <div
      style={wrapperStyle}
      data-element-hit
      data-element-id={el.id}
      onClick={(e) => {
        if (isEditing) return;
        e.stopPropagation();
        onSelect(e);
      }}
      onDoubleClick={handleDoubleClick}
      onMouseDown={(e) => {
        if (isEditing) return;
        onMouseDown?.(e, element);
      }}
    >
      <svg
        width={width}
        height={height}
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        style={svgStyle}
      >
        <path
          d={pathD}
          fill={fillValue}
          stroke={strokeW > 0 ? (stroke?.color ?? '#999') : 'none'}
          strokeWidth={strokeW}
          strokeDasharray={dashArray}
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      <ShapeTextEditor
        text={element.data.text?.content ?? ''}
        textStyle={textStyle}
        isEditing={isEditing}
        onCommit={handleCommit}
        onEditStart={handleEditStart}
        onEditEnd={handleEditEnd}
      />
    </div>
  );
}
